import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import {
  commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems,
  buildCombinedReason, getFacilityDisplayName, formatTIRADSCompact, getTIRADSRecommendation, hasTIRADSFeatures,
  NODULE_COMPOSITION, NODULE_ECHOGENICITY, NODULE_MARGINS, NODULE_SHAPE, ECHOGENIC_FOCI
} from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

// Looks up a descriptor's ACR TI-RADS point value and renders it as
// "value in lowercase (points)" for the compact nodule summary line —
// e.g. composition "Solid" -> "solid (2)". Returns null for an empty value.
function findPoints(options, value) {
  const opt = options.find(o => o.value === value);
  return opt ? opt.points : null;
}

function formatDescriptor(options, value) {
  if (!value) return null;
  const points = findPoints(options, value);
  return points !== null ? `${value.toLowerCase()} (${points})` : value.toLowerCase();
}

export function ThyroidPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const rl = data.rightLobe || {};
  const ll = data.leftLobe || {};
  const isthmus = data.isthmus || {};
  const nodules = data.nodules || [];

  const rlVol = (rl.length && rl.width && rl.depth)
    ? calculateVolume(rl.length, rl.width, rl.depth) : null;
  const llVol = (ll.length && ll.width && ll.depth)
    ? calculateVolume(ll.length, ll.width, ll.depth) : null;

  const measurementsText = [
    (!rl.hemithyroidectomy && rl.length && rl.width && rl.depth)
      ? `Right lobe: ${rl.length} x ${rl.width} x ${rl.depth} cm${rlVol ? ` (Vol: ${rlVol} cc)` : ''}` : null,
    (!ll.hemithyroidectomy && ll.length && ll.width && ll.depth)
      ? `Left lobe: ${ll.length} x ${ll.width} x ${ll.depth} cm${llVol ? ` (Vol: ${llVol} cc)` : ''}` : null,
    (!isthmus.surgicallyAbsent && isthmus.thickness) ? `Isthmus: ${isthmus.thickness} mm` : null,
  ].filter(Boolean).join('\n');

  // Per-lobe display numbering (R1/R2, L1, I1...) — mirrors the form's
  // getNoduleTabLabel() so PDF and form never disagree on which nodule is which.
  const allNodules = nodules.map((n) => {
    const sameLobe = nodules.filter(x => x.lobe === n.lobe);
    const idx = sameLobe.findIndex(x => x.id === n.id) + 1;
    return { ...n, side: n.lobe, idx };
  });

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="THYROID ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            {allNodules.length > 0 && (
              <View style={commonStyles.measurementBox}>
                <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>NODULES</Text>
                {allNodules.map((n, i) => {
                  const noduleVol = (n.length && n.width && n.depth)
                    ? calculateVolume(n.length, n.width, n.depth) : null;
                  const hasTirads = hasTIRADSFeatures(n) && n.tiradsScore !== undefined && n.tiradsScore !== null;
                  const recommendation = hasTirads ? getTIRADSRecommendation(n.tiradsScore, n) : null;
                  // Location, size, and all five scored descriptors concatenated onto
                  // one line — keeps the box compact when several nodules are present.
                  const descriptorLine = [
                    n.location || null,
                    (n.length && n.width && n.depth)
                      ? `${n.length} x ${n.width} x ${n.depth} cm${noduleVol ? ` (${noduleVol} mL)` : ''}` : null,
                    formatDescriptor(NODULE_COMPOSITION, n.composition),
                    formatDescriptor(NODULE_ECHOGENICITY, n.echogenicity),
                    formatDescriptor(NODULE_MARGINS, n.margins),
                    formatDescriptor(NODULE_SHAPE, n.shape),
                    formatDescriptor(ECHOGENIC_FOCI, n.echogenicFoci),
                  ].filter(Boolean).join(', ');
                  return (
                    <View key={i} style={{ marginBottom: 6 }}>
                      <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold' }]}>
                        {n.side} Nodule {n.idx}
                      </Text>
                      {descriptorLine && <Text style={commonStyles.infoBoxText}>{descriptorLine}</Text>}
                      {hasTirads && (
                        <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold' }]}>
                          {formatTIRADSCompact(n.tiradsScore)}
                        </Text>
                      )}
                      {recommendation && (
                        <Text style={[commonStyles.infoBoxText, {
                          color: recommendation === 'No follow-up needed' ? COLORS.normal : COLORS.abnormal,
                          fontFamily: 'Helvetica-Bold'
                        }]}>
                          {recommendation}
                        </Text>
                      )}
                      {n.description && <Text style={commonStyles.infoBoxText}>Description: {n.description}</Text>}
                    </View>
                  );
                })}
              </View>
            )}
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="RIGHT LOBE" />
            {data.thyroidectomy ? (
              <PdfLabelValue label="Status:" value="Thyroidectomy" forceBlack />
            ) : rl.hemithyroidectomy ? (
              <PdfLabelValue label="Status:" value="Right Hemithyroidectomy" forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Echotexture:" value={rl.echotexture || ''} isAbnormal={rl.echotexture && rl.echotexture !== 'Normal'} />
                <PdfLabelValue label="Vascularity:" value={rl.vascularity || ''} isAbnormal={rl.vascularity && rl.vascularity !== 'Normal'} />
              </>
            )}

            <PdfAccentSectionHeader title="LEFT LOBE" />
            {data.thyroidectomy ? (
              <PdfLabelValue label="Status:" value="Thyroidectomy" forceBlack />
            ) : ll.hemithyroidectomy ? (
              <PdfLabelValue label="Status:" value="Left Hemithyroidectomy" forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Echotexture:" value={ll.echotexture || ''} isAbnormal={ll.echotexture && ll.echotexture !== 'Normal'} />
                <PdfLabelValue label="Vascularity:" value={ll.vascularity || ''} isAbnormal={ll.vascularity && ll.vascularity !== 'Normal'} />
              </>
            )}

            <PdfAccentSectionHeader title="ISTHMUS" />
            {data.thyroidectomy ? (
              <PdfLabelValue label="Status:" value="Thyroidectomy" forceBlack />
            ) : isthmus.surgicallyAbsent ? (
              <PdfLabelValue label="Status:" value="Surgically Absent" forceBlack />
            ) : (
              <PdfLabelValue label="Echotexture:" value={isthmus.echotexture || ''} isAbnormal={isthmus.echotexture && isthmus.echotexture !== 'Normal'} />
            )}

            <PdfAccentSectionHeader title="LYMPH NODES" />
            <PdfLabelValue label="Status:" value={data.lymphNodes || ''} isAbnormal={data.lymphNodes && data.lymphNodes !== 'None identified'} />
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
